import * as turf from "@turf/turf";
import { zoneValueLabel } from "@/utils/zoneLabels.js";
import { formatWeatherValue } from "@/features/route/waypointWeather.jsx";

// Coast distance, resonance and via areas stay summary-only (decision 2026-10-21).
const KINDS = [
  ["wind_transits", "wind", "warning"],
  ["wave_transits", "wave", "warning"],
  ["current_transits", "current", "warning"],
  ["hra_transits", "hra", "warning"],
  ["jwla_transits", "jwla", "warning"],
  ["custom_area_transits", "custom_area", "warning"],
  ["eca_transits", "eca", "info"],
];

const MAX_OFFSET_KM = 5;
const SAME_POINT_DEG = 1e-6;

const isLonLat = (v) =>
  Array.isArray(v) && v.length >= 2 && Number.isFinite(v[0]) && Number.isFinite(v[1]);

const unwrapNear = (lon, ref) => lon + 360 * Math.round((ref - lon) / 360);

const mercY = (lat) =>
  (Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) * 180) / Math.PI;
const mercLat = (y) => (Math.atan(Math.exp((y * Math.PI) / 180)) * 360) / Math.PI - 90;

export function transitsOf(summary) {
  if (!summary || typeof summary !== "object") return [];
  return KINDS.flatMap(([key, kind, level]) =>
    (Array.isArray(summary[key]) ? summary[key] : [])
      .filter((t) => isLonLat(t?.entry?.lonlat) && isLonLat(t?.exit?.lonlat))
      .map((t, i) => ({ ...t, id: `${kind}-${i}`, kind, level })),
  );
}

// Measured in Mercator so rhumbline legs and densified greatcircle chords both match.
function locate(lonlat, coords) {
  let best = null;
  for (let i = 0; i < coords.length - 1; i++) {
    const [ax, alat] = coords[i];
    const [bx, blat] = coords[i + 1];
    const ay = mercY(alat);
    const by = mercY(blat);
    const px = unwrapNear(lonlat[0], ax);
    const py = mercY(lonlat[1]);
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    const t = lenSq > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / lenSq)) : 0;
    const qx = ax + t * dx;
    const qy = ay + t * dy;
    const d = (px - qx) ** 2 + (py - qy) ** 2;
    if (!best || d < best.d) best = { d, seg: i, t, at: [qx, mercLat(qy)] };
  }
  return best;
}

// segOwner maps each concatenated segment back to its leg (= wp1 point index).
function legRun(lineFeatures, fromNumber, toNumber) {
  const coords = [];
  const segOwner = [];
  lineFeatures.forEach((f, legIdx) => {
    const number = f?.properties?.analyse?.wp1?.properties?.number;
    const legCoords = f?.geometry?.coordinates;
    if (number == null || number < fromNumber || number > toNumber) return;
    if (!Array.isArray(legCoords) || legCoords.length < 2) return;
    if (coords.length && segOwner[segOwner.length - 1] !== legIdx - 1) return;
    legCoords.forEach(([lon, lat], i) => {
      if (coords.length && i === 0) return;
      const prev = coords[coords.length - 1];
      coords.push([prev ? unwrapNear(lon, prev[0]) : lon, lat]);
      if (coords.length > 1) segOwner.push(legIdx);
    });
  });
  return { coords, segOwner };
}

// Transits with an unknown leg or an end > 5 km off the drawn line are dropped.
export function transitSections(transits, lineGeojson) {
  const lineFeatures = lineGeojson?.features ?? [];
  if (!lineFeatures.length) return [];
  return (transits ?? []).flatMap((transit) => {
    const fromNumber = transit.entry?.leg_waypoint_number;
    const toNumber = transit.exit?.leg_waypoint_number;
    if (!Number.isFinite(fromNumber) || !Number.isFinite(toNumber)) return [];
    const { coords, segOwner } = legRun(lineFeatures, fromNumber, toNumber);
    if (coords.length < 2) return [];
    const entry = locate(transit.entry.lonlat, coords);
    const exit = locate(transit.exit.lonlat, coords);
    const offLine = (raw, hit) =>
      turf.distance(raw, [hit.at[0], hit.at[1]]) > MAX_OFFSET_KM;
    if (offLine(transit.entry.lonlat, entry) || offLine(transit.exit.lonlat, exit)) return [];
    if (exit.seg < entry.seg || (exit.seg === entry.seg && exit.t < entry.t)) return [];
    const firstLeg = segOwner[entry.seg];
    const lastLeg = segOwner[exit.seg];
    return [
      {
        ...transit,
        entryAt: entry.at,
        exitAt: exit.at,
        coordinates: [entry.at, ...coords.slice(entry.seg + 1, exit.seg + 1), exit.at].filter(
          (c, i, all) => i === 0 || c[0] !== all[i - 1][0] || c[1] !== all[i - 1][1],
        ),
        firstPoint: transit.entry.at_departure ? firstLeg : firstLeg + 1,
        lastPoint: transit.exit.at_arrival ? lastLeg + 1 : lastLeg,
      },
    ];
  });
}

const samePoint = (a, b) =>
  Math.abs(unwrapNear(a[0], b[0]) - b[0]) < SAME_POINT_DEG &&
  Math.abs(a[1] - b[1]) < SAME_POINT_DEG;

// Midpoint along the drawn section in Mercator, so it sits on the dashed line.
function sectionMidpoint(coords) {
  const pts = coords.map(([lon, lat]) => [lon, mercY(lat)]);
  const lens = pts.slice(1).map((p, i) => Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]));
  let rest = lens.reduce((a, b) => a + b, 0) / 2;
  for (let i = 0; i < lens.length; i++) {
    if (rest <= lens[i] && lens[i] > 0) {
      const t = rest / lens[i];
      const [ax, ay] = pts[i];
      const [bx, by] = pts[i + 1];
      return [ax + (bx - ax) * t, mercLat(ay + (by - ay) * t)];
    }
    rest -= lens[i];
  }
  return coords[coords.length - 1];
}

const etaMs = (end) => {
  const ms = Date.parse(end?.eta_utc ?? "");
  return Number.isFinite(ms) ? ms : null;
};

// One marker per same-level group overlapping in time, at its longest section's middle.
export function transitMarkers(sections) {
  const sorted = [...(sections ?? [])].sort(
    (a, b) => (etaMs(a.entry) ?? 0) - (etaMs(b.entry) ?? 0),
  );
  const groups = [];
  sorted.forEach((s) => {
    const open = groups.findLast((g) => g.level === s.level);
    const start = etaMs(s.entry);
    const joins =
      open &&
      (start != null && open.end != null
        ? start <= open.end
        : samePoint(sectionMidpoint(open.sections[0].coordinates), sectionMidpoint(s.coordinates)));
    if (joins) {
      open.sections.push(s);
      open.end = Math.max(open.end ?? -Infinity, etaMs(s.exit) ?? -Infinity);
    } else {
      groups.push({ level: s.level, sections: [s], end: etaMs(s.exit) });
    }
  });
  return groups.map(({ level, sections: grouped }) => {
    const longest = grouped.reduce((a, b) => ((b.hours ?? 0) > (a.hours ?? 0) ? b : a));
    return { lonlat: sectionMidpoint(longest.coordinates), level, sections: grouped };
  });
}

// Strongest section level per route point index.
export function transitPointLevels(sections) {
  const levels = new Map();
  (sections ?? []).forEach((s) => {
    for (let i = s.firstPoint; i <= s.lastPoint; i++) {
      if (levels.get(i) !== "warning") levels.set(i, s.level);
    }
  });
  return levels;
}

export function transitLinesGeojson(sections) {
  return {
    type: "FeatureCollection",
    // Red last so it stays on top where it overlaps orange.
    features: (sections ?? [])
      .filter((s) => s.coordinates.length >= 2)
      .sort((a, b) => (a.level === "warning") - (b.level === "warning"))
      .map((s) => ({
        type: "Feature",
        geometry: { type: "LineString", coordinates: s.coordinates },
        properties: { id: s.id, level: s.level },
      })),
  };
}

const cache = new WeakMap();

// Memoised per drawn line object: RoutePoints/RouteLines re-render on every pan.
export function routeTransitSections(route) {
  const lineGeojson = route?.lines?.geojson;
  // A sketch never recalculates, so a summary it inherited would never refresh.
  if (route?.sketch || !lineGeojson || typeof lineGeojson !== "object") return [];
  const summary = route?.summary ?? null;
  const hit = cache.get(lineGeojson);
  if (hit && hit.summary === summary) return hit.sections;
  const sections = transitSections(transitsOf(summary), lineGeojson);
  cache.set(lineGeojson, { summary, sections });
  return sections;
}

// "WP 12 → WP 13" for the leg a transit end lies on; the arrival WP alone.
export function transitLegLabel(pointFeatures, number) {
  if (!Number.isFinite(number)) return null;
  const next = (pointFeatures ?? [])
    .map((f) => f?.properties?.number)
    .filter((n) => Number.isFinite(n) && n > number)
    .reduce((min, n) => (min == null || n < min ? n : min), null);
  return next == null ? `WP ${number}` : `WP ${number} → WP ${next}`;
}

const WEATHER = {
  wind: ["wind", "max_wind_knots", "threshold_wind_knots", "kn"],
  wave: ["waves", "max_wave_height_meter", "threshold_wave_height_meter", "m"],
  current: ["current", "max_current_knots", "threshold_current_knots", "kn"],
};

const ZONES = {
  hra: ["high_risk", (s) => (s.risk_type ? [{ label: `Risk: ${zoneValueLabel(s.risk_type)}` }] : [])],
  jwla: ["war_risk", (s) => (s.peril ? [{ label: `Risk: ${zoneValueLabel(s.peril)}` }] : [])],
  custom_area: ["operational_risk", () => []],
  eca: ["eca", (s) => (s.regime ? [{ label: zoneValueLabel(s.regime) }] : [])],
};

export const num = (v) => (v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);

function cardEndpoint(end, pointFeatures, flag) {
  if (!end) return undefined;
  return {
    time: end.eta_utc ?? null,
    ...(end[flag] === true ? { [flag === "at_departure" ? "atDeparture" : "atArrival"]: true } : {}),
    leg: transitLegLabel(pointFeatures, end.leg_waypoint_number),
    position: end.lonlat,
  };
}

const sameSpan = (a, b) =>
  a.entry?.eta_utc === b.entry?.eta_utc && a.exit?.eta_utc === b.exit?.eta_utc;

// Transit sections → TransitCardBody props; one shared timeline when all spans match.
export function transitCardProps(sections, pointFeatures) {
  const list = sections ?? [];
  const shared = list.length > 0 && list.every((s) => sameSpan(s, list[0]));
  const span = (s) => ({
    entry: cardEndpoint(s.entry, pointFeatures, "at_departure"),
    exit: cardEndpoint(s.exit, pointFeatures, "at_arrival"),
    durationH: num(s.hours) ?? undefined,
  });
  const cardSections = list.map((s) => {
    const own = shared ? {} : span(s);
    if (WEATHER[s.kind]) {
      const [parameter, maxKey, limitKey, unit] = WEATHER[s.kind];
      return { type: "weather", parameter, max: num(s[maxKey]), limit: num(s[limitKey]), unit, ...own };
    }
    const [zoneKind, attributes] = ZONES[s.kind];
    return { type: "zone", zoneKind, name: s.name ?? "", attributes: attributes(s), ...own };
  });
  return shared ? { sections: cardSections, ...span(list[0]) } : { sections: cardSections };
}

const TABLE_KIND = {
  wind: "wind",
  wave: "wave",
  current: "current",
  hra: "high_risk",
  jwla: "war_risk",
  custom_area: "operational_risk",
  eca: "eca",
};
const WEATHER_UNIT = { wind: "kn", wave: "m", current: "kn" };

function tableEndpoint(end, pointFeatures, flag) {
  return {
    time: end?.eta_utc ?? null,
    legNumber: num(end?.leg_waypoint_number),
    leg: transitLegLabel(pointFeatures, num(end?.leg_waypoint_number)),
    position: end?.lonlat ?? null,
    ...(end?.[flag] === true ? { [flag === "at_departure" ? "atDeparture" : "atArrival"]: true } : {}),
  };
}

const tableCache = new WeakMap();

// route_summary transits → RouteWaypointGrid / RouteTransitStrip items (same source as the map).
// Memoised per summary so the grid's memos survive TablePanel re-renders.
export function transitTableItems(summary, pointFeatures) {
  if (!summary || typeof summary !== "object") return [];
  const hit = tableCache.get(summary);
  if (hit && hit.pointFeatures === pointFeatures) return hit.items;
  const items = transitsOf(summary).map((t) => {
    const kind = TABLE_KIND[t.kind];
    const weather = WEATHER[t.kind];
    return {
      id: t.id,
      kind,
      level: t.level,
      name: t.name ?? null,
      risk: zoneValueLabel(t.risk_type ?? t.peril) ?? null,
      max: weather ? num(t[weather[1]]) : null,
      limit: weather ? num(t[weather[2]]) : null,
      unit: WEATHER_UNIT[kind] ?? null,
      entry: tableEndpoint(t.entry, pointFeatures, "at_departure"),
      exit: tableEndpoint(t.exit, pointFeatures, "at_arrival"),
      durationH: num(t.hours),
    };
  });
  tableCache.set(summary, { pointFeatures, items });
  return items;
}

const WEATHER_LABEL = { wind: "Wind", wave: "Waves", current: "Current" };
const ZONE_LABEL = {
  high_risk: ["High risk", "High risk area"],
  war_risk: ["War risk", "War risk area"],
  operational_risk: ["Op. risk", "Operational risk area"],
  eca: ["ECA", "ECA"],
};

// Short for strip bands ("Wind 42", "War risk · Red Sea"), long for issues and tooltips.
export function transitLabel(item, { long = false } = {}) {
  if (WEATHER_LABEL[item.kind]) {
    const value = item.max != null ? formatWeatherValue(item.max, item.kind) : "–";
    if (!long) return `${WEATHER_LABEL[item.kind]} ${value}`;
    const limit = item.limit != null ? ` · limit ${formatWeatherValue(item.limit, item.kind)}` : "";
    return `${WEATHER_LABEL[item.kind]} ${value} ${item.unit}${limit}`;
  }
  const [short, full] = ZONE_LABEL[item.kind] ?? [item.kind, item.kind];
  return [long ? full : short, item.name, long && item.risk ? `Risk: ${item.risk}` : null]
    .filter(Boolean)
    .join(" · ");
}
